import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpStatus } from '../../common/enums/HttpStatus';
import {
  IQuestion,
  IQuestionSubmission,
} from '../../common/interfaces/Question.interface';
import { IAnswer } from '../../common/interfaces/Answer.interface';
import { IPaginatedResponse } from '../../common/interfaces/PaginatedResponse.interface';
import { createBaseQuery } from './utils/createBaseQuery';

export const flowApi = createApi({
  reducerPath: 'flowApi',
  baseQuery: createBaseQuery('/flows'),
  tagTypes: ['Flow'],
  endpoints: (builder) => ({
    fetchQuestions: builder.query<
      { code: HttpStatus; data: IPaginatedResponse<IQuestion> },
      { page: number; size: number }
    >({
      query: ({ page, size }) => `/questions/?page=${page}&size=${size}`,
      providesTags: (result) =>
        result
          ? [
              { type: 'Flow' as const, id: 'LIST' },
              ...result.data.data.map((question) => ({
                type: 'Flow' as const,
                id: question.id,
              })),
            ]
          : [{ type: 'Flow', id: 'LIST' }],
    }),
    fetchActiveQuestions: builder.query<
      { code: HttpStatus; data: IQuestion[] },
      void
    >({
      query: () => ({
        url: '/questions/active',
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'Flow' as const, id: 'LIST' },
              ...result.data.map((question) => ({
                type: 'Flow' as const,
                id: question.id,
              })),
            ]
          : [{ type: 'Flow', id: 'LIST' }],
    }),
    createQuestion: builder.mutation<IQuestion, IQuestionSubmission>({
      query: (newQuestion) => ({
        url: '/questions',
        method: 'POST',
        body: { questions: [newQuestion] },
      }),
      invalidatesTags: [{ type: 'Flow', id: 'LIST' }],
    }),
    fetchAnswers: builder.query<{ code: HttpStatus; data: IAnswer[] }, void>({
      query: () => ({
        url: '/answers',
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'Flow' as const, id: 'LIST' },
              ...result.data.map((answer) => ({
                type: 'Flow' as const,
                id: answer.id,
              })),
            ]
          : [{ type: 'Flow', id: 'LIST' }],
    }),
    updateQuestion: builder.mutation<
      IQuestion,
      { updatedQuestion: IQuestionSubmission }
    >({
      query: ({ updatedQuestion }) => ({
        url: `/questions/${updatedQuestion.id}`,
        method: 'PATCH',
        body: updatedQuestion,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Flow', id: 'LIST' },
        { type: 'Flow', id: arg.updatedQuestion.id },
      ],
    }),
    createAnswer: builder.mutation<IAnswer, Partial<IAnswer>>({
      query: (newAnswer) => ({
        url: '/answers',
        method: 'POST',
        body: { answers: [newAnswer] },
      }),
      invalidatesTags: [{ type: 'Flow', id: 'LIST' }],
    }),
    updateAnswer: builder.mutation<IAnswer, Partial<IAnswer>>({
      query: (updatedAnswer) => ({
        url: `/answers/${updatedAnswer.id}`,
        method: 'PATCH',
        body: updatedAnswer,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Flow', id: 'LIST' },
        { type: 'Flow', id },
      ],
    }),
  }),
});

export const {
  useFetchQuestionsQuery,
  useFetchActiveQuestionsQuery,
  useFetchAnswersQuery,
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
} = flowApi;
